import React, { useEffect, useState, useMemo } from 'react';
import { useUserContext } from '../../context';
import { useApi } from '../../hooks/useApi';
import { Dialog, DialogBody, Switch, Typography, Card, CardBody, CardHeader } from '@material-tailwind/react';
import { ClockLoader } from 'react-spinners';

const tailwindTextColors = {
    checkIn: "text-green-600",
    checkOut: "text-red-600",
    payment: "text-black",
    expense: "text-orange-500",
};

export default function ActivityTracker() {
    const { fetchAccessToken, defaultHotel, assignments } = useUserContext();
    const { getActiveCheckInsByRange, getCheckedInRoomsByRange, getCheckedOutRoomsByRange, getPayments, getExpenses } = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => getCurrentDate());
    const [activeCheckIns, setActiveCheckIns] = useState([]);
    const [checkIns, setCheckIns] = useState([]);
    const [checkOuts, setCheckOuts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [showExpenses, setShowExpenses] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!defaultHotel) return;
            setIsLoading(true);
            try {
                const accessToken = await fetchAccessToken();
                const startTime = getEpoch(selectedDate, "00:00");
                const endTime = getEpoch(selectedDate, "23:59");
                const [
                    activeCheckInsData,
                    checkInsData,
                    checkOutsData,
                    paymentsData,
                    expensesData
                ] = await Promise.all([
                    getActiveCheckInsByRange(defaultHotel, startTime, endTime, accessToken),
                    getCheckedInRoomsByRange(defaultHotel, startTime, endTime, accessToken),
                    getCheckedOutRoomsByRange(defaultHotel, startTime, endTime, accessToken),
                    getPayments(defaultHotel, startTime, endTime, accessToken),
                    getExpenses(defaultHotel, startTime, endTime, accessToken),
                ]);

                const safeSetState = (data, setter, transform = d => d) => {
                    setter(Array.isArray(data) && data.length ? transform(data) : []);
                };

                safeSetState(checkInsData, setCheckIns);
                safeSetState(checkOutsData, setCheckOuts);
                safeSetState(activeCheckInsData, setActiveCheckIns);
                safeSetState(paymentsData, setPayments);
                safeSetState(expensesData, setExpenses, data => data.filter(expense => expense.cash));
            } catch (error) {
                alert("Error fetching data: " + error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [defaultHotel, selectedDate]);


    function getCurrentDate(daysAdd = 0) {
        let d = new Date();
        d.setDate(d.getDate() + daysAdd);
        d.setHours(0, 0, 0, 0)
        return d;
    }

    function formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    let gotoPreviousDate = () => {
        const previousDay = new Date(selectedDate);
        previousDay.setDate(selectedDate.getDate() - 1);
        setSelectedDate(previousDay);
    }

    let gotonextDate = () => {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(selectedDate.getDate() + 1);
        setSelectedDate(nextDay);
    }

    function getEpoch(date, time, seconds = 0) {
        const [hours, minutes] = time.split(':').map(Number);
        const combinedDate = new Date(date);
        combinedDate.setHours(hours);
        combinedDate.setMinutes(minutes);
        combinedDate.setSeconds(seconds);
        combinedDate.setMilliseconds(seconds * 100);

        return combinedDate.getTime();
    }

    const activityData = useMemo(() => {
        const activity = [];
        for (const checkIn of activeCheckIns) {
            activity.push({
                type: 'checkIn',
                cardNo: checkIn.cardNo,
                roomNo: checkIn.roomNo,
                guestName: checkIn.guestName,
                time: checkIn.checkInEpoch,
                amount: '-',
                mode: checkIn.bookingType
            });
        }
        for (const checkIn of checkIns) {
            activity.push({
                type: 'checkIn',
                cardNo: checkIn.cardNo,
                roomNo: checkIn.roomNo,
                guestName: checkIn.guestName,
                time: checkIn.checkInEpoch,
                amount: '-',
                mode: checkIn.bookingType,
                remarks: ""
            });
        }
        for (const checkOut of checkOuts) {
            activity.push({
                type: 'checkOut',
                cardNo: checkOut.cardNo,
                roomNo: checkOut.roomNo,
                guestName: checkOut.guestName,
                time: checkOut.checkOutEpoch,
                amount: '-',
                mode: checkOut.bookingType,
                remarks: ""
            });
        }
        for (const payment of payments) {
            activity.push({
                type: 'payment',
                cardNo: payment.cardNo,
                roomNo: payment.roomNos,
                guestName: payment.guestName,
                time: payment.paymentDateTime,
                amount: payment.paymentAmount,
                mode: payment.paymentMode,
                remarks: payment.remarks || ""
            });
        }
        for (const expense of expenses) {
            activity.push({
                type: 'expense',
                cardNo: '-',
                roomNo: '-',
                guestName: '-',
                time: expense.expenseDateTime,
                amount: expense.expenseAmount,
                mode: 'Cash Expense',
                remarks: expense.remarks || ""
            });
        }
        return activity;
    }, [activeCheckIns, checkIns, checkOuts, payments, expenses]);

    const paymentTotalMap = useMemo(() => {
        const map = new Map();
        payments.forEach(payment => {
            if (!map.has(payment.paymentMode)) {
                map.set(payment.paymentMode, { total: 0 });
            }
            map.get(payment.paymentMode).total += payment.paymentAmount;
        });
        return map;
    }, [payments]);

    const expenseTotal = useMemo(() => {
        return expenses.reduce((total, expense) => total + expense.expenseAmount, 0);
    }, [expenses]);

    const selectedDateString = formatDate(selectedDate);

    function formatEpochToDate(epochMillis) {
        const date = new Date(epochMillis);
        const pad = (n) => n.toString().padStart(2, '0');

        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }

    function formatEpochToTime(epochMillis) {
        const date = new Date(epochMillis);
        const pad = (n) => n.toString().padStart(2, '0');

        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());

        return `${hours}:${minutes}`;
    }


    return (
        <div className="activity-tracker">
            <Card className="h-full w-full">
                <CardHeader variant="gradient" color="gray" className="mt-3 mb-3 p-3 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        Activity Tracker
                    </Typography>
                    <Switch checked={showExpenses} onChange={(e) => setShowExpenses(!showExpenses)} color="green" label={<Typography className="font-normal text-white" > Expenses </Typography>} />
                </CardHeader>
                <CardBody className="overflow-scroll px-3 pt-2 pb-2">
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={gotoPreviousDate} className="ml-2 p-1 bg-green-300 rounded hover:bg-green-500">
                            {"<<<"}
                        </button>
                        <Typography variant="h6" color="black">
                            {selectedDateString}
                        </Typography>
                        <button onClick={gotonextDate} className="m-1 p-1 bg-green-300 rounded hover:bg-green-500">
                            {">>>"}
                        </button>
                    </div>
                    <table className="table-auto w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="px-1 py-1 border">S.No</th>
                                <th className="px-1 py-1 border">Date & Time</th>
                                <th className="px-1 py-1 border">Card No</th>
                                <th className="px-1 py-1 border">Room No</th>
                                <th className="px-1 py-1 border">Mode</th>
                                <th className="px-1 py-1 border">Amount</th>
                                <th className="px-1 py-1 border">Guest Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activityData.sort((a, b) => a.time - b.time).filter(a => showExpenses || a.type !== "expense").map((activity, index) => (
                                <tr key={index} className={`hover:bg-gray-100 ${tailwindTextColors[activity.type]}`}>
                                    <td className="px-1 py-1 border">{index + 1}</td>
                                    <td className="px-1 py-1 border whitespace-nowrap">
                                        {formatEpochToDate(activity.time)}
                                        <br />
                                        {formatEpochToTime(activity.time)}
                                    </td>
                                    <td className="px-1 py-1 border">{activity.cardNo}</td>
                                    <td className="px-1 py-1 border">{activity.roomNo}</td>
                                    <td className="px-1 py-1 border">{activity.mode}</td>
                                    <td className="px-1 py-1 border">
                                        {activity.amount}
                                        {activity.remarks && (
                                            <span
                                                className="ml-1 text-blue-600 cursor-pointer font-bold"
                                                title={activity.remarks}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    alert(activity.remarks);
                                                }}
                                            >
                                                (Adv)
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-1 py-1 border uppercase">{activity.guestName}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="mt-4">
                        <Typography variant="h6" color="black">
                            Summary
                        </Typography>
                        <table className="table-auto w-full text-left border-collapse mt-2">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th className="px-1 py-1 border">Activity Type</th>
                                    <th className="px-1 py-1 border">Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="hover:bg-gray-100">
                                    <td className="px-1 py-1 border">Check-ins</td>
                                    <td className="px-1 py-1 border">{activeCheckIns.length + checkIns.length}</td>
                                </tr>
                                <tr className="hover:bg-gray-100">
                                    <td className="px-1 py-1 border">Check-outs</td>
                                    <td className="px-1 py-1 border">{checkOuts.length}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4">
                        <Typography variant="h6" color="black">
                            Payments Summary
                        </Typography>
                        <table className="table-auto w-full text-left border-collapse mt-2">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th className="px-1 py-1 border">Payment Mode</th>
                                    <th className="px-1 py-1 border">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from(paymentTotalMap.entries()).map(([mode, { total }], index) => (
                                    <tr key={index} className="hover:bg-gray-100">
                                        <td className="px-1 py-1 border">{mode}</td>
                                        <td className="px-1 py-1 border">{total}.00</td>
                                    </tr>
                                ))}
                                <tr className="hover:bg-gray-100">
                                    <td className="px-1 py-1 border">Total</td>
                                    <td className="px-1 py-1 border">
                                        {Array.from(paymentTotalMap.values()).reduce((sum, { total }) => sum + total, 0)}.00
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4">
                        <Typography variant="h6" color="black">
                            Total Expenses
                        </Typography>
                        <table className="table-auto w-full text-left border-collapse mt-2">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th className="px-1 py-1 border">Description</th>
                                    <th className="px-1 py-1 border">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="hover:bg-gray-100">
                                    <td className="px-1 py-1 border">Cash Expenses</td>
                                    <td className="px-1 py-1 border">{expenseTotal}.00</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>
            <Dialog open={isLoading} className="bg-transparent shadow-none z-60" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>

        </div>
    );
}